import { useEffect, useMemo, useState, Fragment } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ColumnDef } from "@tanstack/react-table";
import { TablePaginate } from "../../../../components/table";
import { Dialog, Transition } from "@headlessui/react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Form from "./components/Form";

import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { useNotifyContext, NotifyType } from "../../../../contexts/NotifyContext";

import { Button } from "../../../../components/form";
import { AuthShield } from "../../../auth/core";
import { Head } from "../../../../components/core";

type Dock = {
  id?: string;
  name: string;
  createdAt: string;
  status: boolean;
};

const FETCH_DOCKS = gql`
  query FetchDocks {
    fetchDocks {
      id
      name
      createdAt
      status
    }
  }
`;

const DockList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { addNotify } = useNotifyContext();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [fetchDocks, { loading }] =
    useLazyQuery(FETCH_DOCKS);
  const [docks, setDocks] = useState<Dock[]>([]);
  const [activeDock, setActiveDock] = useState<
    Dock | undefined
  >(undefined);
  const [newDock, setNewDock] = useState<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    fetchDocks()
      .then(({ data, error }) => {
        if (data?.fetchDocks) {
          const updatedDocks = data.fetchDocks.map(
            (item: Dock) => ({
              ...item,
              createdAt: DateTime.fromISO(item.createdAt, {
                locale: i18n.language,
              }).toLocaleString(DateTime.DATE_MED),
            })
          );
          setDocks(updatedDocks);
        } else {
          return navigate("/error/401", {
            state: {
              message: error
                ? error.message
                : "Docks read dock(s) is required to access this page.",
            },
          });
        }
      })
      .catch((error) => {
        return navigate("/error/500", {
          state: {
            error,
            message: error.message,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDocks]);

  const UPDATE_DOCK = gql`
    mutation DockUpdate(
      $id: ID!
      $name: String!
      $status: Boolean!
    ) {
      dockUpdate(
        input: {
          id: $id
          params: { name: $name, status: $status }
        }
      ) {
        dock {
          id
          name
          createdAt
          status
        }
      }
    }
  `;
  const [updateDock] = useMutation(UPDATE_DOCK);
  const handleUpdate = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    updateDock({
      variables: {
        id: activeDock?.id,
        name: values.name,
        status: String(values.status) === "true" ? true : false,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.dockUpdate) {
          const newDocks = [...docks];
          const index = newDocks.findIndex(
            (dock) => dock.id === activeDock?.id
          );
          newDocks[index] = {
            ...data.dockUpdate.dock,
            createdAt: DateTime.fromISO(
              data.dockUpdate.dock.createdAt,
              {
                locale: i18n.language,
              }
            ).toLocaleString(DateTime.DATE_MED),
          };
          setDocks(newDocks);
          setActiveDock(undefined);
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Dock update failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Dock update failed",
          message: error.message,
        });
      });
  };

  const CREATE_DOCK = gql`
    mutation DockCreate(
      $name: String!
      $status: Boolean!
    ) {
      dockCreate(
        input: {
          params: { name: $name, status: $status }
        }
      ) {
        dock {
          id
          name
          createdAt
          status
        }
      }
    }
  `;
  const [createDock] = useMutation(CREATE_DOCK);
  const handleCreate = (
    values: any,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    createDock({
      variables: {
        name: values.name,
        status: String(values.status) === "true" ? true : false,
      },
    })
      .then(({ data }) => {
        actions.setSubmitting(false);
        if (data?.dockCreate) {
          setDocks([
            ...docks,
            {
              ...data.dockCreate?.dock,
              createdAt: DateTime.fromISO(
                data.dockCreate.dock.createdAt,
                {
                  locale: i18n.language,
                }
              ).toLocaleString(DateTime.DATE_MED),
            },
          ]);
          setNewDock(undefined);
          addNotify({
            type: NotifyType.SUCCESS,
            title: "Dock created successfully",
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Dock create failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Dock create failed",
          message: error.message,
        });
      });
  };

  const DELETE_DOCK = gql`
    mutation DockDelete($id: ID!) {
      dockDelete(input: { id: $id }) {
        dock {
          id
          name
        }
      }
    }
  `;
  const [deleteDock] = useMutation(DELETE_DOCK);
  const handleDelete = async (id: string) => {
    deleteDock({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.dockDelete) {
          const deletedDock = data.dockDelete?.dock;
          const newDocks = [...docks];
          const index = newDocks.findIndex(
            (dock) => dock.id === id
          );
          newDocks.splice(index, 1);
          setDocks(newDocks);

          addNotify({
            type: NotifyType.SUCCESS,
            title: `Dock ${deletedDock.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Dock deletion failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Dock deletion failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<Dock>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
        meta: {
          className: "w-auto md:w-20",
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: (props) => (
          <div>
            <AuthShield access={["update-docks"]}>
              <button
                type="button"
                className="mr-4 appearance-none py-2 text-primary-700 hover:text-primary-900"
                onClick={() => setActiveDock(props.row.original)}
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </button>
            </AuthShield>
            <AuthShield access={["destroy-docks"]}>
              <button
                type="button"
                className="appearance-none py-2 text-red-700 hover:text-red-900"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id!,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </button>
            </AuthShield>
          </div>
        ),
        meta: {
          className: "w-auto md:w-20",
        },
      },
    ],
    [i18n.language]
  );

  return (
    <>
      <Head
        title="Docks"
        heading="Docks"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: DockListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              Docks List
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the docks in your account including their
              name, title, email and dock.
            </p>
          </div>
        </div>

        <TablePaginate
          data={docks}
          columns={columns}
          loading={loading}
          className="mt-4"
          renderSubComponent={() => (
            <AuthShield access={["create-docks"]}>
              <Button onClick={() => setNewDock(true)}>
                Add dock
              </Button>
            </AuthShield>
          )}
        />
      </div>

      <Transition.Root
        show={activeDock ? true : false}
        as={Fragment}
        appear
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setActiveDock(undefined);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-md transform divide-y divide-gray-100 rounded-3xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <Form
                  heading="Edit Dock - "
                  initialValues={{
                    id: activeDock?.id,
                    name: activeDock?.name ?? "",
                    status: activeDock?.status ?? true,
                  }}
                  onSubmit={handleUpdate}
                  submitLabel={t("text_update")}
                  onCancel={() => setActiveDock(undefined)}
                  cancelLabel={t("text_cancel")}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root
        show={newDock ? true : false}
        as={Fragment}
        appear
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setNewDock(undefined);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-md transform divide-y divide-gray-100 rounded-3xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <Form
                  heading="New Dock - "
                  initialValues={{
                    name: "",
                    status: true,
                  }}
                  onSubmit={handleCreate}
                  submitLabel={t("text_create")}
                  onCancel={() => setNewDock(undefined)}
                  cancelLabel={t("text_cancel")}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default DockList;
export const DockListResource: ResourceProps = {
  name: "Docks List",
  description: "A list of docks",
  access: ["read-docks"],
  path: "docks",
};
